import { createNewCognitoUserSession } from './cognitoHelpers';

const NONCE_COOKIE = 'im_sso_nonce';
const NONCE_COOKIE_DOMAIN = '.invoicemaker.com';

const readNonceCookie = () => {
    const match = document.cookie.match(
        new RegExp(`(?:^|; )${NONCE_COOKIE}=([^;]*)`)
    );
    return match ? decodeURIComponent(match[1]) : null;
};

// One-time use: always clear the handshake cookie once we've read it.
const clearNonceCookie = () => {
    document.cookie = `${NONCE_COOKIE}=; Domain=${NONCE_COOKIE_DOMAIN}; Path=/; Max-Age=0; Secure; SameSite=Lax`;
};

/**
 * Cross-origin login handoff from the Invoice Maker marketing site.
 *
 * The marketing site performs the Cognito login itself, then redirects here with
 * the session tokens in the URL *fragment*:
 *
 *   https://app.invoicemaker.com/#im_sso=<base64url(JSON.stringify({ id, refresh, access }))>
 *
 * This reads those tokens, hydrates the Cognito session into localStorage via the
 * existing helper, and strips the fragment — all synchronously, before React mounts.
 *
 * HARD RULES (load-bearing — see plans/marketing-unified-login-plan.md):
 *  - MUST stay fully synchronous. No await, no promises, no dynamic import.
 *    index.js calls this immediately before root.render(); any async gap would let
 *    React Router mount and read the URL before the session/hash are settled (race).
 *  - MUST be CALLED from index.js (not imported for its side effect). This package
 *    sets "sideEffects": false, so a bare `import './ssoHandoff'` would be
 *    tree-shaken out of the production build and never run.
 *  - The fragment is used (not a query param) so the token is never sent to the
 *    server, never appears in Referer, and survives any hosting-level redirect.
 *
 * @return {boolean} true if a valid handoff was ingested, false otherwise.
 */
export const ingestSsoHandoff = () => {
    const hash = window.location.hash || '';
    const match = hash.match(/im_sso=([^&]+)/);
    if (!match) {
        return false;
    }

    // Strip the fragment immediately, before analytics/Sentry init can capture
    // the URL. Keeps the raw tokens out of any logging.
    window.history.replaceState(
        null,
        '',
        window.location.pathname + window.location.search
    );

    // Read + clear the handshake cookie before doing anything with the tokens.
    const expectedNonce = readNonceCookie();
    clearNonceCookie();

    try {
        const { id, refresh, access, nonce } = JSON.parse(
            atob(match[1].replace(/-/g, '+').replace(/_/g, '/'))
        );

        // Nonce handshake: only accept tokens that arrive with a nonce matching
        // the cookie the marketing site set in THIS browser. Blocks a crafted
        // #im_sso link from injecting an attacker's session (login-CSRF). A
        // missing cookie (e.g. a link opened out of the blue) fails closed.
        if (!expectedNonce || !nonce || nonce !== expectedNonce) {
            return false;
        }

        if (!id || !refresh || !access) {
            return false;
        }

        // NB: argument order is (idToken, refreshToken, accessToken).
        createNewCognitoUserSession(id, refresh, access);
        return true;
    } catch (err) {
        return false;
    }
};
